import type {BuilderOutlineWithIdMapAndTargets} from '../aiBuilderGeneration'
import type {Pointer} from '@wix/document-services-types'

export type AnalysisScope = 'section' | 'page' | 'site'

export interface BrandContext {
    voice: string
    colors: string[]
    fonts: string[]
    layoutStyle?: string
    mediaStyle?: string
}

export interface BusinessContext {
    siteName: string
    siteDescription: string
    industry?: string
    services?: string[]
    location?: string
    keywords?: string[]
}

export interface SiteContext {
    brand: BrandContext
    businessContext: BusinessContext
}

export interface SiteMetadata {
    siteName?: string
    brandColors?: string[]
    brandFonts?: string[]
}

export interface AnalyzeParams {
    scope: AnalysisScope
    targetPointer: Pointer
    siteContext: SiteContext
    text?: BuilderOutlineWithIdMapAndTargets
    color?: ColorData
    font?: FontData
}

export interface ColorData {
    usedColors: string[]
    brandColors: string[]
}

export interface FontData {
    usedFonts: string[]
    brandFonts: string[]
}

export interface Signal {
    textTopics?: string[]
    headings?: string[]
    paragraphs?: string[]
    componentCount?: number
    usedColors?: string[]
    usedFonts?: string[]
}

export interface InsightDetails {
    message: string
    expectedKeywords?: string[]
    foundKeywords?: string[]
    offBrandColors?: string[]
    brandColors?: string[]
}

export interface Insight {
    id: string
    type: 'TEXT_DOMAIN_MISMATCH' | 'COLOR_INCONSISTENCY' | 'FONT_INCONSISTENCY'
    confidence: number
    impact: 'high' | 'medium' | 'low'
    scope: AnalysisScope
    targetPointer: Pointer
    details: InsightDetails
}

export interface AnalysisResult {
    scope: AnalysisScope
    targetPointer: Pointer
    signals: Signal
    insights: Insight[]
}

export interface RecommendParams {
    analysisResult: AnalysisResult
    maxRecommendations?: number
    scoreThreshold?: number
}

export interface RecommendationScores {
    impact: number
    confidence: number
    readiness: number
    final: number
}

export interface RecommendationAction {
    type: 'REPLACE_PLACEHOLDER' | 'APPLY_BRAND_COLORS' | 'REWRITE_TEXT'
}

export interface Recommendation {
    id: string
    scope: AnalysisScope
    recommendation: string
    description: string
    category: 'content' | 'design' | 'branding'
    priority: 'high' | 'medium' | 'low'
    actions: RecommendationAction[]
    scores: RecommendationScores
    insightId: string
}

export interface RecommendationResult {
    shouldRecommend: boolean
    recommendations: Recommendation[]
}
